import { Eye, SquarePen, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CountryRiskBadge } from '@/components/country-risk-badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { TableCell, TableRow } from '@/components/ui/table';
import { ProductionPlaceDialog } from '@/features/production-declaration/components/statement/production-place/production-place-dialog';
import { ProductionPlaceViewDialog } from '@/features/production-declaration/components/statement/production-place/production-place-view-dialog';
import { useCanEditProductionDeclaration } from '@/features/production-declaration/hooks/use-can-edit-production-declaration';
import { useLocalizedCountryName } from '@/hooks/use-localized-country';
import { useDeleteProductionPlace } from '@/services/apis/mutations/production-declaration/use-delete-production-place';
import { useGetCountries } from '@/services/apis/queries/country/use-get-countries';
import type { ProductionPlace } from '@/types/core/production-declaration/production-place';
import type { ProductionDeclarationStatusCode as ProductionDeclarationStatusCodeType } from '@/types/core/purchase-order/production-declaration-status-code';

type ProductionDeclarationMaterialPlacesListItemProps = {
  commodityOriginId: string;
  materialId: string;
  productionDeclarationId: string;
  productionPlace: ProductionPlace;
  statusCode: ProductionDeclarationStatusCodeType;
  token?: string;
};

export function ProductionDeclarationMaterialPlacesListItem({
  commodityOriginId,
  materialId,
  productionDeclarationId,
  productionPlace,
  statusCode,
  token,
}: ProductionDeclarationMaterialPlacesListItemProps) {
  const { t } = useTranslation('productionDeclaration', {
    useSuspense: false,
  });
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const { isPending, mutate: deleteProductionPlace } =
    useDeleteProductionPlace();
  const canManage = useCanEditProductionDeclaration(statusCode);
  const isInternalUser = token === undefined;
  const { data: countries = [] } = useGetCountries();
  const { getLocalizedCountry } = useLocalizedCountryName(undefined, 'none');
  const country = countries.find(
    (item) => item.id === productionPlace.countryId,
  );
  const countryName = country?.isoCode
    ? (getLocalizedCountry(country.isoCode) ?? country.isoCode)
    : t('countryRisk.countryUnavailable', { ns: 'common' });

  const handleDelete = () => {
    deleteProductionPlace(
      {
        commodityOriginId,
        materialId,
        productionDeclarationId,
        productionPlaceId: productionPlace.id,
        token,
      },
      {
        onSuccess: () => {
          setIsDeleteDialogOpen(false);
        },
      },
    );
  };

  const onDeleteDialogOpenChange = (nextOpen: boolean) => {
    if (isPending) {
      return;
    }

    setIsDeleteDialogOpen(nextOpen);
  };

  return (
    <>
      <TableRow className='border-declaration-border-light hover:bg-transparent'>
        <TableCell className='whitespace-normal px-0 py-5 font-medium text-[1.05rem] text-declaration-foreground leading-7'>
          {productionPlace.description ??
            t('materialCommodityOrigins.cards.productionPlace.unnamedLocation')}
          {isInternalUser ? (
            <div className='mt-2 flex flex-wrap items-center gap-2 font-normal text-sm'>
              <span className='min-w-0 break-words'>{countryName}</span>
              <CountryRiskBadge
                context='recorded'
                risk={productionPlace.countryRiskLevel}
              />
            </div>
          ) : null}
        </TableCell>
        <TableCell className='px-0 py-5 text-[1.05rem] text-declaration-foreground-muted leading-7'>
          <span className='inline-flex min-h-11 items-center rounded-full border border-declaration-foreground px-5 text-declaration-foreground'>
            {productionPlace.forestType?.label ?? '—'}
          </span>
        </TableCell>
        <TableCell className='px-0 py-5'>
          <div className='flex justify-end gap-0'>
            {canManage ? (
              <Button
                aria-label={t(
                  'materialCommodityOrigins.cards.productionPlace.actions.delete',
                )}
                className='h-auto p-0 text-declaration-foreground shadow-none transition-colors hover:bg-transparent hover:text-declaration-foreground-muted dark:text-declaration-foreground dark:hover:bg-transparent dark:hover:text-declaration-foreground-muted'
                disabled={isPending}
                onClick={() => {
                  setIsDeleteDialogOpen(true);
                }}
                type='button'
                variant='ghost'
              >
                <Trash2 className='size-[1.35rem]' strokeWidth={2.1} />
              </Button>
            ) : null}
            {canManage ? (
              <Button
                aria-label={t(
                  'materialCommodityOrigins.cards.productionPlace.actions.edit',
                )}
                className='h-auto p-0 text-declaration-foreground shadow-none transition-colors hover:bg-transparent hover:text-declaration-foreground-muted dark:text-declaration-foreground dark:hover:bg-transparent dark:hover:text-declaration-foreground-muted'
                onClick={(event) => {
                  event.preventDefault();
                  setIsEditDialogOpen(true);
                }}
                type='button'
                variant='ghost'
              >
                <SquarePen className='size-[1.35rem]' strokeWidth={2.1} />
              </Button>
            ) : isInternalUser ? (
              <Button
                aria-label={t(
                  'materialCommodityOrigins.cards.productionPlace.actions.view',
                )}
                className='h-auto p-0 text-declaration-foreground shadow-none transition-colors hover:bg-transparent hover:text-declaration-foreground-muted dark:text-declaration-foreground dark:hover:bg-transparent dark:hover:text-declaration-foreground-muted'
                onClick={() => {
                  setIsViewDialogOpen(true);
                }}
                type='button'
                variant='ghost'
              >
                <Eye className='size-[1.35rem]' strokeWidth={2.1} />
              </Button>
            ) : null}
          </div>
        </TableCell>
      </TableRow>
      {isInternalUser && !canManage ? (
        <ProductionPlaceViewDialog
          onOpenChange={setIsViewDialogOpen}
          open={isViewDialogOpen}
          productionPlace={productionPlace}
        />
      ) : null}
      {isEditDialogOpen ? (
        <ProductionPlaceDialog
          canSubmit={canManage}
          commodityOriginId={commodityOriginId}
          materialId={materialId}
          onOpenChange={setIsEditDialogOpen}
          open={isEditDialogOpen}
          productionDeclarationId={productionDeclarationId}
          productionPlace={productionPlace}
          token={token}
        />
      ) : null}
      <Dialog onOpenChange={onDeleteDialogOpenChange} open={isDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t(
                'materialCommodityOrigins.cards.productionPlace.actions.deleteModalTitle',
              )}
            </DialogTitle>
            <DialogDescription>
              {t(
                'materialCommodityOrigins.cards.productionPlace.actions.deleteModalDescription',
              )}
            </DialogDescription>
          </DialogHeader>
          <div className='flex w-full justify-end gap-2'>
            <DialogClose asChild>
              <Button disabled={isPending} type='button' variant='outline'>
                {t('cancel', { ns: 'common' })}
              </Button>
            </DialogClose>
            <Button
              disabled={isPending}
              onClick={handleDelete}
              type='button'
              variant='destructive'
            >
              {t('delete', { ns: 'common' })}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
